import { ArrowLeft, House, RotateCcw, type LucideIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate, useRouteError } from "react-router"
import { getBootstrapError } from "@/app/bootstrap/bootstrap-error"
import { routeTree } from "@/app/router"
import { Button } from "@/shadcn/components/ui/button"

const FALLBACK_ERROR_MESSAGE = "Something went wrong"

function normalizeErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message
  }

  if (typeof error === "string" && error.trim()) {
    return error
  }

  if (typeof error === "undefined" || error === null) {
    return null
  }

  if (typeof error === "object") {
    try {
      const serializedError = JSON.stringify(error)
      if (serializedError && serializedError !== "{}") {
        return serializedError
      }
    } catch {
      return FALLBACK_ERROR_MESSAGE
    }
  }

  return String(error)
}

type ErrorPageAction = {
  label: string
  onClick: () => void
  icon: LucideIcon
  variant?: "default" | "outline"
}

type ErrorPageContentProps = {
  title: string
  message: string
  actions: ErrorPageAction[]
}

function ErrorPageContent({ title, message, actions }: ErrorPageContentProps) {
  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <div className="flex max-w-xl flex-col items-center gap-4 text-center">
        <h1 className="text-2xl font-medium sm:text-3xl">{title}</h1>
        <p className="text-base font-medium text-red-600 sm:text-lg">{message}</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {actions.map((action) => (
            <Button
              key={action.label}
              iconLeft={action.icon}
              variant={action.variant ?? "default"}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        </div>
      </div>
    </main>
  )
}

export function AppErrorPage() {
  const navigate = useNavigate()
  const routeError = useRouteError()
  const { t } = useTranslation("common")

  const handleGoHome = () => {
    navigate(routeTree.root.path(), { replace: true })
  }

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    navigate(routeTree.root.path(), { replace: true })
  }

  return (
    <ErrorPageContent
      title={t("somethingWentWrong")}
      message={normalizeErrorMessage(routeError) ?? t("somethingWentWrong")}
      actions={[
        {
          label: t("notFound.goHome"),
          onClick: handleGoHome,
          icon: House,
        },
        {
          label: t("back"),
          onClick: handleGoBack,
          icon: ArrowLeft,
          variant: "outline",
        },
      ]}
    />
  )
}

export default function BootstrapErrorPage() {
  const bootstrapError = getBootstrapError()

  const handleRetry = () => {
    window.location.replace(routeTree.root.path())
  }

  return (
    <ErrorPageContent
      title="Error while initializing the app"
      message={normalizeErrorMessage(bootstrapError) ?? FALLBACK_ERROR_MESSAGE}
      actions={[
        {
          label: "Retry",
          onClick: handleRetry,
          icon: RotateCcw,
        },
      ]}
    />
  )
}
